import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Receipt,
  Person,
  Email,
  CalendarToday,
  Payment,
  ArrowBack,
} from '@mui/icons-material';
import { useAppTheme } from '@contexts/ThemeContext';
import httpClient from '@utils/httpClient';

interface VerificationResult {
  verified: boolean;
  employee_name: string;
  employee_email: string;
  payroll_period: string;
  pay_date: string;
  gross_salary: number;
  message: string;
}

const VerifyPayslip: React.FC = () => {
  const { barcodeData } = useParams<{ barcodeData: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<VerificationResult | null>(null);

  useEffect(() => {
    const verifyBarcode = async () => {
      // Get barcode from URL params or query string
      const barcode = barcodeData || searchParams.get('code');

      if (!barcode) {
        setError('No barcode data provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await httpClient.get(`/hr/payroll/verify-barcode/${barcode}`);

        if (response.success && response.data) {
          setResult(response.data);
        } else {
          setError(response.error?.message || 'Failed to verify payslip');
        }
      } catch (err: any) {
        console.error('Verification error:', err);
        setError(err.message || 'Failed to verify payslip. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    verifyBarcode();
  }, [barcodeData, searchParams]);

  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: isDarkMode ? '#121212' : '#f5f5f5',
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3, color: isDarkMode ? '#fff' : '#000' }}>
            Verifying Payslip...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: isDarkMode ? '#121212' : '#f5f5f5',
      py: 4,
    }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Receipt sx={{ fontSize: 60, color: isDarkMode ? '#90caf9' : '#1976d2', mb: 2 }} />
          <Typography variant="h4" fontWeight="bold" sx={{ color: isDarkMode ? '#fff' : '#000', mb: 1 }}>
            Payslip Verification
          </Typography>
          <Typography variant="body2" sx={{ color: isDarkMode ? '#aaa' : '#666' }}>
            Scan barcode or enter code to verify authenticity
          </Typography>
        </Box>

        {/* Error State */}
        {error && (
          <Card sx={{
            bgcolor: isDarkMode ? '#1e1e1e' : '#fff',
            boxShadow: 3,
            borderRadius: 2,
          }}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <ErrorIcon sx={{ fontSize: 80, color: '#f44336', mb: 2 }} />
              <Typography variant="h5" fontWeight="bold" sx={{ color: isDarkMode ? '#fff' : '#000', mb: 2 }}>
                Verification Failed
              </Typography>
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
              <Typography variant="body2" sx={{ color: isDarkMode ? '#aaa' : '#666', mb: 3 }}>
                This payslip could not be verified. Please check:
              </Typography>
              <Box sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto', mb: 3 }}>
                <Typography variant="body2" sx={{ color: isDarkMode ? '#aaa' : '#666', mb: 1 }}>
                  • Barcode is scanned correctly
                </Typography>
                <Typography variant="body2" sx={{ color: isDarkMode ? '#aaa' : '#666', mb: 1 }}>
                  • Payslip is from this organization
                </Typography>
                <Typography variant="body2" sx={{ color: isDarkMode ? '#aaa' : '#666', mb: 1 }}>
                  • Document has not been tampered with
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/')}
                sx={{ mt: 2 }}
              >
                Back to Home
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Success State */}
        {result && result.verified && (
          <Card sx={{
            bgcolor: isDarkMode ? '#1e1e1e' : '#fff',
            boxShadow: 3,
            borderRadius: 2,
          }}>
            <CardContent>
              {/* Success Header */}
              <Box sx={{ textAlign: 'center', mb: 3, pt: 2 }}>
                <CheckCircle sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
                <Chip
                  label="VERIFIED AUTHENTIC"
                  color="success"
                  sx={{ fontWeight: 'bold', fontSize: '0.9rem', px: 2 }}
                />
                <Typography variant="h5" fontWeight="bold" sx={{ color: isDarkMode ? '#fff' : '#000', mt: 2 }}>
                  Payslip Verified Successfully
                </Typography>
                <Typography variant="body2" sx={{ color: isDarkMode ? '#aaa' : '#666', mt: 1 }}>
                  This payslip is authentic and issued by the organization
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Payslip Details */}
              <Typography variant="h6" fontWeight="bold" sx={{ color: isDarkMode ? '#fff' : '#000', mb: 3 }}>
                Payslip Details
              </Typography>

              {/* Employee Name */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                <Person sx={{ fontSize: 24, color: isDarkMode ? '#90caf9' : '#1976d2', mr: 2 }} />
                <Box>
                  <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666', display: 'block' }}>
                    Employee Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" sx={{ color: isDarkMode ? '#fff' : '#000' }}>
                    {result.employee_name}
                  </Typography>
                </Box>
              </Box>

              {/* Employee Email */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                <Email sx={{ fontSize: 24, color: isDarkMode ? '#90caf9' : '#1976d2', mr: 2 }} />
                <Box>
                  <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666', display: 'block' }}>
                    Email Address
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" sx={{ color: isDarkMode ? '#fff' : '#000' }}>
                    {result.employee_email}
                  </Typography>
                </Box>
              </Box>

              {/* Payroll Period */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                <CalendarToday sx={{ fontSize: 24, color: isDarkMode ? '#90caf9' : '#1976d2', mr: 2 }} />
                <Box>
                  <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666', display: 'block' }}>
                    Payroll Period
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" sx={{ color: isDarkMode ? '#fff' : '#000' }}>
                    {result.payroll_period}
                  </Typography>
                </Box>
              </Box>

              {/* Pay Date */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                <CalendarToday sx={{ fontSize: 24, color: isDarkMode ? '#90caf9' : '#1976d2', mr: 2 }} />
                <Box>
                  <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666', display: 'block' }}>
                    Payment Date
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" sx={{ color: isDarkMode ? '#fff' : '#000' }}>
                    {result.pay_date}
                  </Typography>
                </Box>
              </Box>

              {/* Gross Salary */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                <Payment sx={{ fontSize: 24, color: isDarkMode ? '#90caf9' : '#1976d2', mr: 2 }} />
                <Box>
                  <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666', display: 'block' }}>
                    Gross Salary
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: isDarkMode ? '#4caf50' : '#2e7d32' }}>
                    Rs.{result.gross_salary.toLocaleString('en-IN')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Footer Note */}
              <Alert severity="info" sx={{ mb: 2 }}>
                This verification confirms that the payslip was issued by the organization and has not been tampered with.
              </Alert>

              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666', display: 'block', mb: 1 }}>
                  Verified at: {new Date().toLocaleString('en-IN')}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={() => navigate('/')}
                  size="small"
                >
                  Back to Home
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default VerifyPayslip;
